import React, { useEffect, useState, useCallback, useMemo } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link as RouterLink } from "react-router-dom";
import { DollarSign, Banknote, Receipt, Clock, CheckCircle2, TrendingUp, Users, Inbox, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import ConfirmDialog from "@/components/ConfirmDialog";

function fmtMoney(n) {
  return `$${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return "—"; }
}

export default function AdminPaymentsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("requests"); // requests | affiliates | payouts | sales
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState(null);
  const [sales, setSales] = useState(null);
  const [salesStatus, setSalesStatus] = useState("all");
  const [requests, setRequests] = useState(null);
  const [requestsStatus, setRequestsStatus] = useState("pending");

  const reloadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/payments/summary");
      setSummary(data);
    } catch {
      // 403 handled by Navigate below
    }
  }, []);

  const reloadRequests = useCallback(async (status) => {
    setRequests(null);
    try {
      const { data } = await api.get(`/admin/payments/payout-requests?status=${status || "all"}`);
      setRequests(data);
    } catch {
      setRequests([]);
    }
  }, []);

  useEffect(() => {
    reloadSummary();
  }, [reloadSummary]);

  useEffect(() => {
    if (tab !== "requests") return;
    reloadRequests(requestsStatus);
  }, [tab, requestsStatus, reloadRequests]);

  useEffect(() => {
    if (tab !== "payouts" || payouts !== null) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/payments/payouts");
        setPayouts(data);
      } catch {
        setPayouts([]);
      }
    })();
  }, [tab, payouts]);

  useEffect(() => {
    if (tab !== "sales") return;
    setSales(null);
    (async () => {
      try {
        const { data } = await api.get(`/admin/payments/sales?status=${salesStatus}`);
        setSales(data);
      } catch {
        setSales([]);
      }
    })();
  }, [tab, salesStatus]);

  const handleAction = useCallback(async (requestId, action) => {
    try {
      await api.post(`/admin/payments/payout-requests/${requestId}/${action}`);
      toast.success(action === "approve" ? "Payout approved & marked paid" : "Request rejected, balance refunded");
      // Refresh both the requests list and the summary tiles
      await Promise.all([reloadRequests(requestsStatus), reloadSummary()]);
      // Also bust payouts cache since approving creates a new payout row
      setPayouts(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    }
  }, [reloadRequests, reloadSummary, requestsStatus]);

  const handleBulkApprove = useCallback(async (requestIds) => {
    try {
      const { data } = await api.post("/admin/payments/payout-requests/bulk-approve", { request_ids: requestIds });
      const total = (data.total_amount || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const msg = data.skipped_count > 0
        ? `Approved ${data.approved_count} ($${total}) · skipped ${data.skipped_count}`
        : `Approved ${data.approved_count} payouts ($${total} total)`;
      toast.success(msg);
      await Promise.all([reloadRequests(requestsStatus), reloadSummary()]);
      setPayouts(null);
      return data;
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || "Bulk approve failed";
      toast.error(detail);
      throw e;
    }
  }, [reloadRequests, reloadSummary, requestsStatus]);

  if (!user) return null;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const totals = summary?.totals;

  return (
    <DashboardLayout>
      <div className="mb-8">
        <div className="label-eyebrow text-[#002FA7] flex items-center gap-2">
          <DollarSign size={12} /> Admin · Payments
        </div>
        <h1 className="text-4xl sm:text-5xl tracking-tight font-black text-[#111827]">Affiliates & payments</h1>
        <p className="text-sm text-[#4B5563] mt-2 max-w-2xl">
          Everything across the system — pending commissions, payment history, every recorded sale, and which affiliates have earned what.
        </p>
      </div>

      {/* Global totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="admin-payments-totals">
        <SummaryCard testid="payments-total-pending" label="Total pending" value={fmtMoney(totals?.total_pending)} icon={Clock} accent />
        <SummaryCard testid="payments-total-paid" label="Total paid (lifetime)" value={fmtMoney(totals?.total_paid)} icon={CheckCircle2} />
        <SummaryCard testid="payments-total-volume" label="Sales volume tracked" value={fmtMoney(totals?.total_volume)} icon={TrendingUp} />
        <SummaryCard testid="payments-payouts-count" label="Payouts processed" value={totals?.total_payouts_count ?? "—"} icon={Banknote} />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E5E7EB] mb-6" data-testid="admin-payments-tabs">
        <Tab id="requests" tab={tab} setTab={setTab} icon={Inbox} badge={totals?.pending_requests_count}>Payout requests</Tab>
        <Tab id="affiliates" tab={tab} setTab={setTab} icon={Users}>Affiliates</Tab>
        <Tab id="payouts" tab={tab} setTab={setTab} icon={Banknote}>Payout history</Tab>
        <Tab id="sales" tab={tab} setTab={setTab} icon={Receipt}>All sales</Tab>
      </div>

      {tab === "requests" && (
        <PayoutRequestsTable
          requests={requests}
          status={requestsStatus}
          setStatus={setRequestsStatus}
          onAction={handleAction}
          onBulkApprove={handleBulkApprove}
        />
      )}
      {tab === "affiliates" && <AffiliatesTable affiliates={summary?.affiliates} />}
      {tab === "payouts" && <PayoutsTable payouts={payouts} />}
      {tab === "sales" && (
        <SalesTable sales={sales} status={salesStatus} setStatus={setSalesStatus} />
      )}
    </DashboardLayout>
  );
}

function Tab({ id, tab, setTab, icon: Icon, badge, children }) {
  const active = tab === id;
  return (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-payments-tab-${id}`}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px transition-colors ${
        active ? "text-[#002FA7] border-[#002FA7]" : "text-[#4B5563] border-transparent hover:text-[#111827]"
      }`}
    >
      <Icon size={14} /> {children}
      {badge > 0 && (
        <span
          data-testid={`admin-payments-tab-${id}-badge`}
          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#DC2626] text-white text-[10px] font-bold"
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function PayoutRequestsTable({ requests, status, setStatus, onAction, onBulkApprove }) {
  const [actingId, setActingId] = useState(null);
  // pending = { request: <row>, action: "approve" | "reject", bulk?: bool }
  // when the admin clicks Approve/Reject — they must confirm in the modal
  // first because both flows immediately mutate balances / mark money as paid.
  const [pending, setPending] = useState(null);
  // Set of request_ids the admin has ticked for bulk approval
  const [selected, setSelected] = useState(new Set());
  // Clear selection whenever the underlying request list changes (filter
  // switched, refresh after action, etc.) so the action bar doesn't show
  // stale ids that have already been approved.
  useEffect(() => {
    setSelected(new Set());
  }, [requests]);

  const pendingRequests = useMemo(
    () => (requests || []).filter((r) => r.status === "pending"),
    [requests]
  );
  const selectedRows = useMemo(
    () => pendingRequests.filter((r) => selected.has(r.request_id)),
    [pendingRequests, selected]
  );
  const selectedTotal = useMemo(
    () => selectedRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
    [selectedRows]
  );
  const allPendingSelected = pendingRequests.length > 0 && selectedRows.length === pendingRequests.length;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === pendingRequests.length ? new Set() : new Set(pendingRequests.map((r) => r.request_id))
    );
  };

  const handle = async () => {
    if (!pending) return;
    if (pending.bulk) {
      const ids = Array.from(selected);
      setActingId("__bulk__");
      try {
        await onBulkApprove(ids);
        setPending(null);
      } catch {
        /* toast already shown by parent */
      } finally {
        setActingId(null);
      }
      return;
    }
    const id = pending.request.request_id;
    const action = pending.action;
    setActingId(`${id}_${action}`);
    try {
      await onAction(id, action);
      setPending(null);
    } finally {
      setActingId(null);
    }
  };

  return (
    <div data-testid="admin-payments-requests">
      {/* Status filter pills */}
      <div className="flex items-center gap-2 mb-4">
        {["pending", "paid", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`requests-filter-${s}`}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border ${
              status === s
                ? "bg-[#111827] text-white border-[#111827]"
                : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#111827]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Bulk action bar — appears when 1+ pending rows are selected. */}
      {selectedRows.length > 0 && (
        <div className="card-flat p-3 mb-3 flex items-center justify-between gap-3 border-[#A7F3D0] bg-[#ECFDF5]" data-testid="bulk-action-bar">
          <div className="text-sm text-[#065F46]">
            <span className="font-bold" data-testid="bulk-selected-count">{selectedRows.length}</span>{" "}
            selected · <span className="font-bold font-mono" data-testid="bulk-selected-total">{fmtMoney(selectedTotal)}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              data-testid="bulk-clear"
              className="text-xs font-bold text-[#4B5563] px-3 py-1.5 rounded-md hover:text-[#111827]"
            >
              Clear
            </button>
            <button
              onClick={() => setPending({ bulk: true, action: "approve" })}
              disabled={actingId === "__bulk__"}
              data-testid="bulk-approve-button"
              className="inline-flex items-center gap-1.5 bg-[#047857] hover:bg-[#065F46] text-white font-bold text-xs px-4 py-2 rounded-md disabled:opacity-50"
            >
              <Check size={12} />
              {actingId === "__bulk__" ? "Marking paid…" : `Approve ${selectedRows.length} selected`}
            </button>
          </div>
        </div>
      )}

      {requests === null ? (
        <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />
      ) : requests.length === 0 ? (
        <div className="card-flat p-12 text-center" data-testid="admin-payments-requests-empty">
          <Inbox size={32} className="mx-auto text-[#9CA3AF] mb-2" />
          <div className="font-display font-black text-xl text-[#111827] mb-1">No {status === "all" ? "" : status} requests</div>
          <p className="text-sm text-[#4B5563]">Affiliates can request a payout from their <span className="font-mono">/dashboard/earnings</span> page once their balance is positive.</p>
        </div>
      ) : (
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr className="text-left">
                  <th className="px-4 py-3 w-10">
                    {pendingRequests.length > 0 && (
                      <input
                        type="checkbox"
                        checked={allPendingSelected}
                        onChange={toggleAll}
                        data-testid="bulk-select-all"
                        aria-label="Select all pending"
                        className="h-4 w-4 rounded border-[#9CA3AF] text-[#002FA7] focus:ring-[#002FA7] cursor-pointer"
                      />
                    )}
                  </th>
                  <Th>Affiliate</Th>
                  <Th right>Amount</Th>
                  <Th>Currency</Th>
                  <Th right>Status</Th>
                  <Th right>Requested</Th>
                  <Th right>Action</Th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const isPending = r.status === "pending";
                  const approveKey = `${r.request_id}_approve`;
                  const rejectKey = `${r.request_id}_reject`;
                  const isSelected = selected.has(r.request_id);
                  return (
                    <tr key={r.request_id} className={`border-b border-[#F3F4F6] hover:bg-[#FAFAFA] ${isSelected ? "bg-[#ECFDF5]" : ""}`} data-testid={`request-row-${r.request_id}`}>
                      <td className="px-4 py-4 w-10">
                        {isPending && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOne(r.request_id)}
                            data-testid={`bulk-select-${r.request_id}`}
                            aria-label={`Select payout request for ${r.affiliate_name || r.affiliate_email}`}
                            className="h-4 w-4 rounded border-[#9CA3AF] text-[#002FA7] focus:ring-[#002FA7] cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          {r.affiliate_picture ? (
                            <img src={r.affiliate_picture} alt={r.affiliate_name} className="h-8 w-8 rounded-full object-cover" />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-xs">
                              {(r.affiliate_name || r.affiliate_email || "?").charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div className="min-w-0">
                            <div className="font-bold text-[#111827] truncate">{r.affiliate_name || "—"}</div>
                            <div className="text-xs text-[#9CA3AF] truncate">{r.affiliate_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right font-mono font-bold text-[#002FA7]" data-testid={`request-amount-${r.request_id}`}>
                        {fmtMoney(r.amount)}
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-[#4B5563]">{r.currency || "GYD"}</td>
                      <td className="px-6 py-4 text-right">
                        <StatusPill status={r.status} />
                      </td>
                      <td className="px-6 py-4 text-right text-xs text-[#4B5563]">{fmtDate(r.created_at)}</td>
                      <td className="px-6 py-4 text-right">
                        {isPending ? (
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => setPending({ request: r, action: "approve" })}
                              disabled={actingId === approveKey || actingId === rejectKey}
                              data-testid={`request-approve-${r.request_id}`}
                              className="inline-flex items-center gap-1 bg-[#047857] hover:bg-[#065F46] text-white font-bold text-xs px-3 py-1.5 rounded-md disabled:opacity-50"
                            >
                              <Check size={12} /> {actingId === approveKey ? "…" : "Approve"}
                            </button>
                            <button
                              onClick={() => setPending({ request: r, action: "reject" })}
                              disabled={actingId === approveKey || actingId === rejectKey}
                              data-testid={`request-reject-${r.request_id}`}
                              className="inline-flex items-center gap-1 bg-white text-[#B91C1C] border border-[#FCA5A5] hover:bg-[#FEF2F2] font-bold text-xs px-3 py-1.5 rounded-md disabled:opacity-50"
                            >
                              <XIcon size={12} /> {actingId === rejectKey ? "…" : "Reject"}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-[#9CA3AF]">
                            {r.approved_at ? `by ${r.approved_by || "admin"}` : r.rejected_at ? `by ${r.rejected_by || "admin"}` : "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirm dialog — every Approve/Reject (single or bulk) passes
          through here so a mis-click can't accidentally mark money as paid
          or refund. */}
      <ConfirmDialog
        open={!!pending}
        tone={pending?.action === "reject" ? "destructive" : "primary"}
        icon={pending?.action === "reject" ? XIcon : Check}
        title={
          pending?.bulk
            ? `Mark ${selectedRows.length} requests (${fmtMoney(selectedTotal)}) as paid?`
            : pending?.action === "approve"
              ? `Mark ${fmtMoney(pending?.request?.amount)} as paid?`
              : `Reject this payout request?`
        }
        description={
          pending?.bulk
            ? `You're confirming you've already paid ${selectedRows.length} affiliates a total of ${fmtMoney(selectedTotal)} GYD outside trackmatrix. Each request will be marked paid and recorded in payout history. Their balances were already debited at request time. Any selected row that's already paid or rejected will be skipped automatically.`
            : pending?.action === "approve"
              ? `You're confirming that you've already paid ${pending?.request?.affiliate_name || pending?.request?.affiliate_email || "this affiliate"} ${fmtMoney(pending?.request?.amount)} ${pending?.request?.currency || "GYD"} outside trackmatrix. This marks the request as paid and records it in their payout history. The balance was already debited when they requested.`
              : `${fmtMoney(pending?.request?.amount)} ${pending?.request?.currency || "GYD"} will be refunded to ${pending?.request?.affiliate_name || pending?.request?.affiliate_email || "this affiliate"}'s balance so they can re-request later. The request will be marked rejected.`
        }
        confirmLabel={
          pending?.bulk
            ? `Yes, mark all ${selectedRows.length} as paid`
            : pending?.action === "approve"
              ? "Yes, mark as paid"
              : "Yes, reject & refund"
        }
        busy={actingId !== null}
        busyLabel={pending?.action === "reject" ? "Refunding…" : "Marking paid…"}
        onCancel={() => actingId === null && setPending(null)}
        onConfirm={handle}
      />
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    pending: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
    paid: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]",
    rejected: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  };
  const cls = styles[status] || "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${cls}`}>
      {status}
    </span>
  );
}

function SummaryCard({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF]">{label}</div>
        <Icon size={16} className={accent ? "text-[#002FA7]" : "text-[#4B5563]"} />
      </div>
      <div className={`font-display font-black text-3xl mt-2 ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value ?? "—"}</div>
    </div>
  );
}

function AffiliatesTable({ affiliates }) {
  if (affiliates === undefined || affiliates === null) {
    return <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />;
  }
  if (affiliates.length === 0) {
    return (
      <div className="card-flat p-12 text-center text-sm text-[#4B5563]">
        No affiliates have earned commissions yet. Record a sale on a lead and the affiliate will appear here.
      </div>
    );
  }
  return (
    <div className="card-flat overflow-hidden" data-testid="admin-payments-affiliates">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left">
              <Th>Affiliate</Th>
              <Th right>Pending</Th>
              <Th right>Paid (lifetime)</Th>
              <Th right>Sales volume</Th>
              <Th right>Sales</Th>
              <Th right>Last payout</Th>
            </tr>
          </thead>
          <tbody>
            {affiliates.map((a) => (
              <tr key={a.user_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`admin-payments-affiliate-row-${a.user_id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {a.picture ? (
                      <img src={a.picture} alt={a.name} className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-xs">
                        {(a.name || a.email || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-[#111827] truncate flex items-center gap-1">
                        {a.name}
                        {a.role === "admin" && <span className="text-[9px] tracking-wider uppercase font-bold bg-[#002FA7] text-white px-1.5 py-0.5 rounded">Admin</span>}
                      </div>
                      <div className="text-xs text-[#9CA3AF] truncate">{a.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right font-mono font-bold text-[#002FA7]" data-testid={`affiliate-pending-${a.user_id}`}>
                  {fmtMoney(a.pending_commission)}
                  {(a.available_balance > 0 || a.pending_payout_request > 0) && (
                    <div className="text-[10px] font-normal text-[#4B5563] mt-0.5 font-sans">
                      {a.available_balance > 0 && <span title="Balance not yet requested">avail {fmtMoney(a.available_balance)}</span>}
                      {a.available_balance > 0 && a.pending_payout_request > 0 && " · "}
                      {a.pending_payout_request > 0 && <span className="text-[#92400E] font-bold" title="Pending payout request">req {fmtMoney(a.pending_payout_request)}</span>}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 text-right font-mono text-[#166534]">{fmtMoney(a.paid_commission)}</td>
                <td className="px-6 py-4 text-right font-mono text-[#111827]">{fmtMoney(a.sales_volume)}</td>
                <td className="px-6 py-4 text-right text-[#111827]">{a.sales_count}</td>
                <td className="px-6 py-4 text-right text-xs text-[#4B5563]">
                  {a.last_payout ? (
                    <>
                      <div>{fmtMoney(a.last_payout.affiliate_amount)}</div>
                      <div className="text-[#9CA3AF]">{fmtDate(a.last_payout.paid_at)}</div>
                    </>
                  ) : <span className="text-[#9CA3AF]">Never paid</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PayoutsTable({ payouts }) {
  if (payouts === null) return <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />;
  if (payouts.length === 0) {
    return <div className="card-flat p-12 text-center text-sm text-[#4B5563]">No payouts processed yet.</div>;
  }
  return (
    <div className="card-flat overflow-hidden" data-testid="admin-payments-payouts">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
            <tr className="text-left">
              <Th>Affiliate</Th>
              <Th right>Total paid</Th>
              <Th right>Own / Referral</Th>
              <Th right>Sales</Th>
              <Th right>Date</Th>
            </tr>
          </thead>
          <tbody>
            {payouts.map((p) => (
              <tr key={p.payout_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`admin-payouts-row-${p.payout_id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {p.affiliate_picture ? (
                      <img src={p.affiliate_picture} alt={p.affiliate_name} className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-xs">
                        {(p.affiliate_name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-[#111827] truncate">{p.affiliate_name || "—"}</div>
                      <div className="text-xs text-[#9CA3AF] truncate">{p.affiliate_email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right font-mono font-bold text-[#166534]">{fmtMoney(p.total_amount)}</td>
                <td className="px-6 py-4 text-right text-xs text-[#4B5563]">
                  <div>Own: <span className="font-mono text-[#111827]">{fmtMoney(p.affiliate_amount)}</span></div>
                  {p.referrer_amount > 0 && <div>Ref: <span className="font-mono text-[#111827]">{fmtMoney(p.referrer_amount)}</span></div>}
                </td>
                <td className="px-6 py-4 text-right text-[#111827]">{(p.affiliate_sale_count || 0) + (p.referrer_sale_count || 0)}</td>
                <td className="px-6 py-4 text-right text-xs text-[#4B5563]">{fmtDate(p.paid_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SalesTable({ sales, status, setStatus }) {
  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        {["all", "pending", "paid"].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`admin-sales-filter-${s}`}
            className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full transition-colors ${
              status === s ? "bg-[#111827] text-white" : "bg-[#F3F4F6] text-[#4B5563] hover:bg-[#E5E7EB]"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {sales === null ? (
        <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />
      ) : sales.length === 0 ? (
        <div className="card-flat p-12 text-center text-sm text-[#4B5563]">No sales match this filter.</div>
      ) : (
        <div className="card-flat overflow-hidden" data-testid="admin-payments-sales">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                <tr className="text-left">
                  <Th>Affiliate</Th>
                  <Th>Lead</Th>
                  <Th right>Amount</Th>
                  <Th right>Commission</Th>
                  <Th right>Status</Th>
                  <Th right>Recorded</Th>
                </tr>
              </thead>
              <tbody>
                {sales.map((s) => (
                  <tr key={s.sale_id} className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`admin-sales-row-${s.sale_id}`}>
                    <td className="px-6 py-4">
                      <div className="font-bold text-[#111827] truncate">{s.affiliate_name || "—"}</div>
                      <div className="text-xs text-[#9CA3AF] truncate">{s.affiliate_email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-[#111827] truncate">{s.lead_name || "—"}</div>
                      {s.lead_email && <div className="text-xs text-[#9CA3AF] truncate">{s.lead_email}</div>}
                      {s.note && <div className="text-xs text-[#9CA3AF] italic mt-0.5">"{s.note}"</div>}
                    </td>
                    <td className="px-6 py-4 text-right font-mono text-[#111827]">{fmtMoney(s.amount)}</td>
                    <td className="px-6 py-4 text-right font-mono font-bold text-[#002FA7]">{fmtMoney(s.affiliate_commission)}</td>
                    <td className="px-6 py-4 text-right">
                      {s.affiliate_paid_out ? (
                        <span className="inline-flex items-center rounded-full bg-[#DCFCE7] text-[#166534] text-xs font-bold px-2 py-0.5">Paid</span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-[#FEF3C7] text-[#92400E] text-xs font-bold px-2 py-0.5">Pending</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right text-xs text-[#4B5563]">{fmtDate(s.recorded_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function Th({ children, right }) {
  return (
    <th className={`px-6 py-3 text-[10px] tracking-wider uppercase font-bold text-[#9CA3AF] ${right ? "text-right" : ""}`}>{children}</th>
  );
}
